import React, { useState, useEffect } from "react";
import { fetchCustomFields, CustomField, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/providers/AuthProvider";
import { useTranslation } from "react-i18next";

interface CandidateCustomFieldsEditTabProps {
  candidate: any;
  onChange: (field: string, value: any) => void;
  isEditing: boolean;
}

export function CandidateCustomFieldsEditTab({ candidate, onChange, isEditing }: CandidateCustomFieldsEditTabProps) {
  const { currentOrganization } = useAuth();
  const organizationId = currentOrganization?.id;
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  // Fetch field definitions
  useEffect(() => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    fetchCustomFields(organizationId)
      .then((data: CustomFieldsJSON) => {
        const defs = data.Candidates || [];
        setFields(defs);
      })
      .catch(() => {
        setError("Failed to load custom fields");
      })
      .finally(() => setLoading(false));
  }, [organizationId]);

  const handleFieldChange = (fieldName: string, value: any) => {
    // Update the custom_fields object in the candidate
    const updatedCustomFields = {
      ...candidate.custom_fields,
      [fieldName]: value
    };
    onChange('custom_fields', updatedCustomFields);
  };

  if (!organizationId) {
    return <div className="p-4 text-red-600">No organization selected.</div>;
  }
  
  if (loading) {
    return <div className="p-4">Loading custom fields...</div>;
  }
  
  if (error) {
    return <div className="p-4 text-red-600">{error}</div>;
  }
  
  if (!fields.length) {
    return <div className="p-4 text-gray-500">No custom fields configured for Candidates yet.</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{t("candidates.customFields.title", "Custom Fields")}</h3>
        <p className="text-gray-500 text-sm">{t("candidates.customFields.description", "Manage custom fields for this candidate.")}</p>
      </div>
      
      <div className="space-y-4">
        {fields.map((field) => {
          const currentValue = candidate.custom_fields && candidate.custom_fields[field.name] !== undefined
            ? candidate.custom_fields[field.name]
            : "";
          
          return (
            <div key={field.name} className="flex flex-col gap-2">
              <label className="font-medium text-sm">{field.name}</label>
              
              {field.type === "text" && (
                <Input
                  value={currentValue}
                  onChange={e => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.name}
                  disabled={!isEditing}
                  aria-label={field.name}
                />
              )}
              
              {field.type === "number" && (
                <Input
                  type="number"
                  value={currentValue}
                  onChange={e => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.name}
                  disabled={!isEditing}
                  aria-label={field.name}
                />
              )}
              
              {field.type === "boolean" && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!currentValue}
                    onCheckedChange={checked => isEditing && handleFieldChange(field.name, checked)}
                    id={field.name}
                    disabled={!isEditing}
                    aria-label={field.name}
                  />
                  <label htmlFor={field.name} className="text-sm">{field.name}</label>
                </div>
              )}
              
              {field.type === "dropdown" && field.options && (
                <Select
                  value={currentValue}
                  onValueChange={val => isEditing && handleFieldChange(field.name, val)}
                  disabled={!isEditing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("candidates.customFields.selectOption", { field: field.name })} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
} 